// ═══════════════════════════════════════════════════════════
// ARAMS Dashboard — Connect Project (AI-Powered Wizard)
//
// Flow:
//   1. GitHub must be connected (redirect to settings if not)
//   2. User provides: project name, target URL, picks GitHub repo + branch
//   3. AI agent analyzes repo → auto-creates project, roles, suites
//
// No manual prompts. ALL automated.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/lib/api';
import type { RepoAnalysisResult } from '@/lib/api';
import {
  Card,
  PageHeader,
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui';
import {
  Github,
  ArrowLeft,
  ArrowRight,
  Rocket,
  Check,
  Shield,
  Sparkles,
  Loader2,
  Search,
  Lock,
  FolderOpen,
  XCircle,
  CheckCircle2,
  Brain,
  FileSearch,
  Code2,
  TestTubes,
  Boxes,
  Globe,
  HelpCircle,
  AlertTriangle,
  Info,
} from 'lucide-react';
import toast from 'react-hot-toast';

const MotionCard = motion.create(Card);

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);
}

// ── Tooltip helper ──────────────────────────────────────

function Tooltip({ text }: { text: string }) {
  return (
    <div className="group relative inline-flex ml-1.5">
      <HelpCircle className="w-3.5 h-3.5 text-surface-500 cursor-help" />
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg bg-surface-800 border border-surface-700 text-xs text-surface-300 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 w-64 text-left shadow-xl">
        {text}
      </div>
    </div>
  );
}

// ── Phase indicator for the AI agent animation ──────────

const AI_PHASES = [
  { icon: FileSearch, label: 'Scanning repository structure…' },
  { icon: Code2, label: 'Analyzing codebase & tech stack…' },
  { icon: Shield, label: 'Detecting authentication flows…' },
  { icon: TestTubes, label: 'Generating test plan & suites…' },
  { icon: Boxes, label: 'Mapping user roles & scenarios…' },
  { icon: Rocket, label: 'Creating project, roles & suites…' },
];

// ══════════════════════════════════════════════════════════
export default function ProjectFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Step: 1 = setup, 2 = AI working
  const [step, setStep] = useState(1);

  // Form fields
  const [projectName, setProjectName] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [repoFullName, setRepoFullName] = useState('');
  const [branch, setBranch] = useState('');
  const [repoSearch, setRepoSearch] = useState('');

  // AI agent animation
  const [aiPhase, setAiPhase] = useState(0);

  // GitHub status
  const { data: githubStatus } = useQuery({
    queryKey: ['github-status'],
    queryFn: () => api.getGithubUserStatus(),
  });

  // Repo list from GitHub
  const { data: repos, isLoading: reposLoading } = useQuery({
    queryKey: ['github-repos'],
    queryFn: () => api.listGithubRepos(),
    enabled: !!githubStatus?.connected,
  });

  // Branch list — fetched from GitHub when repo is selected
  const { data: branches, isLoading: branchesLoading } = useQuery({
    queryKey: ['github-branches', repoFullName],
    queryFn: () => {
      const [owner, repo] = repoFullName.split('/');
      return api.listGithubBranches(owner!, repo!);
    },
    enabled: !!repoFullName && repoFullName.includes('/') && !!githubStatus?.connected,
  });

  // Auto-select default branch when branches load
  useEffect(() => {
    if (branches && branches.length > 0 && !branch) {
      const repo = repos?.find((r: any) => (r.full_name || r.fullName) === repoFullName);
      const defaultBranch = (repo as any)?.default_branch || (repo as any)?.defaultBranch || 'main';
      const exists = branches.some((b: any) => b.name === defaultBranch);
      setBranch(exists ? defaultBranch : branches[0]?.name || 'main');
    }
  }, [branches]); // eslint-disable-line react-hooks/exhaustive-deps

  // Analyze + auto-create mutation chain
  const analyzeAndCreate = useMutation({
    mutationFn: async () => {
      // AI analyzes the repo
      const data = await api.analyzeRepo(repoFullName, branch);
      const a: RepoAnalysisResult = data.analysis;

      // Phase 5: Creating project
      setAiPhase(5);

      const configYaml = JSON.stringify({
        testCategories: a.testCategories || [],
        suggestedTests: a.suggestedTests || [],
        userRoles: a.userRoles || [],
        securityChecks: a.securityChecks || [],
        techStack: a.techStack || [],
        missingInfo: a.missingInfo || [],
        analyzedWith: data.model,
        analyzedAt: new Date().toISOString(),
      });

      const finalName =
        projectName.trim() || a.projectName || repoFullName.split('/')[1] || 'New Project';
      const finalSlug = slugify(finalName);

      const project = await api.createProject({
        name: finalName,
        slug: finalSlug,
        description: a.description || '',
        githubRepo: repoFullName,
        githubBranch: branch,
        targetUrl: targetUrl.trim() || a.targetUrl || data.repoInfo?.homepage || '',
        auth: a.auth
          ? {
              loginUrl: a.auth.loginPath,
              usernameSelector: a.auth.usernameSelector,
              passwordSelector: a.auth.passwordSelector,
              submitSelector: a.auth.submitSelector,
            }
          : undefined,
        configYaml,
      } as any);

      const projectId = (project as any).projectId;

      // Auto-create test roles from AI analysis
      if (a.userRoles?.length) {
        for (const role of a.userRoles) {
          try {
            await api.createRole(projectId, {
              roleName: role.name,
              roleEmail: `${role.name.toLowerCase().replace(/\s+/g, '.')}@test.local`,
              roleBusinessType: role.description?.substring(0, 100),
              roleDescription: role.description,
              roleLandingPage: role.landingPage,
            } as any);
          } catch {
            /* non-fatal */
          }
        }
      }

      // Auto-create test suites from AI analysis
      if (a.suggestedTests?.length) {
        const categories = new Map<string, typeof a.suggestedTests>();
        for (const test of a.suggestedTests) {
          const cat = test.category || 'general';
          if (!categories.has(cat)) categories.set(cat, []);
          categories.get(cat)!.push(test);
        }
        for (const [category, tests] of categories) {
          try {
            await api.createSuite(projectId, {
              suiteName:
                category.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) + ' Suite',
              suiteType: tests[0]?.type || 'e2e',
              suitePriority: tests[0]?.priority || 'normal',
              suiteTags: [category, ...tests.map((t) => t.type).filter(Boolean)].filter(
                (v, i, a) => a.indexOf(v) === i,
              ),
              suiteDescription: tests.map((t) => t.name).join(', '),
            } as any);
          } catch {
            /* non-fatal */
          }
        }
      }

      return project;
    },
    onSuccess: (project: any) => {
      toast.success(t('projectWizard.projectCreated'));
      navigate(`/projects/${project.projectId}`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Auto-name from repo on selection
  useEffect(() => {
    if (repoFullName && !projectName) {
      const repoName = repoFullName.split('/')[1] || '';
      setProjectName(repoName.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()));
    }
  }, [repoFullName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-set target URL from repo homepage
  useEffect(() => {
    if (repoFullName && repos && !targetUrl) {
      const repo = repos.find((r: any) => (r.full_name || r.fullName) === repoFullName) as any;
      if (repo?.homepage) setTargetUrl(repo.homepage);
    }
  }, [repoFullName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cycle through AI phases during analysis
  useEffect(() => {
    if (step !== 2 || !analyzeAndCreate.isPending) return;
    const timer = setInterval(() => {
      setAiPhase((p) => (p < AI_PHASES.length - 1 ? p + 1 : p));
    }, 3000);
    return () => clearInterval(timer);
  }, [step, analyzeAndCreate.isPending]);

  const filteredRepos = repos?.filter(
    (r: any) =>
      !repoSearch ||
      (r.full_name || r.fullName || '').toLowerCase().includes(repoSearch.toLowerCase()),
  );

  const canLaunch = repoFullName.includes('/') && branch;

  const handleLaunch = () => {
    if (!canLaunch) {
      toast.error(t('projectWizard.invalidRepo'));
      return;
    }
    setStep(2);
    setAiPhase(0);
    analyzeAndCreate.mutate();
  };

  const anim = (i: number) => ({
    initial: { opacity: 0, y: 20 } as const,
    animate: { opacity: 1, y: 0 } as const,
    transition: { delay: i * 0.08, duration: 0.35 },
  });

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <Button variant="ghost" onClick={() => navigate('/projects')} className="mb-4">
        <ArrowLeft className="w-4 h-4" />
        {t('nav.projects')}
      </Button>

      <PageHeader
        title={t('projectWizard.title')}
        subtitle={step === 1 ? t('projectWizard.simpleSubtitle') : t('projectWizard.agentWorking')}
      />

      {/* Step indicator */}
      <div className="flex items-center gap-3 mb-8">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
            step >= 1 ? 'bg-brand-600 text-white' : 'bg-surface-800 text-surface-500'
          }`}
        >
          {step > 1 ? <Check className="w-4 h-4" /> : '1'}
        </div>
        <span
          className={`text-sm font-medium ${step >= 1 ? 'text-surface-200' : 'text-surface-500'}`}
        >
          {t('projectWizard.stepSetup')}
        </span>
        <div className={`w-12 h-0.5 ${step > 1 ? 'bg-brand-500' : 'bg-surface-800'}`} />
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
            step >= 2
              ? 'bg-brand-600 text-white ring-2 ring-brand-400/30'
              : 'bg-surface-800 text-surface-500'
          }`}
        >
          {analyzeAndCreate.isSuccess ? <Check className="w-4 h-4" /> : '2'}
        </div>
        <span
          className={`text-sm font-medium ${step >= 2 ? 'text-surface-200' : 'text-surface-500'}`}
        >
          {t('projectWizard.stepAgent')}
        </span>
      </div>

      <AnimatePresence mode="wait">
        {/* ═══ STEP 1: Setup ═══════════════════════════════ */}
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-6"
          >
            {!githubStatus?.connected ? (
              <MotionCard {...anim(0)}>
                <div className="flex items-center gap-4 p-4">
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center border border-red-500/20">
                    <AlertTriangle className="w-6 h-6 text-red-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-surface-100">
                      {t('projectWizard.githubRequired')}
                    </h3>
                    <p className="text-sm text-surface-400 mt-1">
                      {t('projectWizard.githubRequiredDesc')}
                    </p>
                  </div>
                  <Button onClick={() => navigate('/settings')}>
                    <Shield className="w-4 h-4" />
                    {t('projectWizard.goToSettings')}
                  </Button>
                </div>
              </MotionCard>
            ) : (
              <>
                {/* Project Name + Target URL */}
                <MotionCard {...anim(0)}>
                  <h3 className="text-lg font-semibold text-surface-100 mb-1 flex items-center gap-2">
                    <Rocket className="w-5 h-5 text-brand-400" />
                    {t('projectWizard.projectDetails')}
                    <Tooltip text={t('projectWizard.projectDetailsTip')} />
                  </h3>
                  <p className="text-sm text-surface-400 mb-4">
                    {t('projectWizard.projectDetailsDesc')}
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-surface-300 mb-1.5 flex items-center gap-1">
                        {t('projectWizard.projectName')}
                        <Tooltip text={t('projectWizard.projectNameTip')} />
                      </label>
                      <Input
                        placeholder={t('projectWizard.projectNamePlaceholder')}
                        value={projectName}
                        onChange={(e) => setProjectName(e.target.value)}
                      />
                      <p className="text-xs text-surface-500 mt-1">
                        {t('projectWizard.projectNameHint')}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-surface-300 mb-1.5 flex items-center gap-1">
                        <Globe className="w-3.5 h-3.5 text-surface-400" />
                        {t('projectWizard.targetUrl')}
                        <Tooltip text={t('projectWizard.targetUrlTip')} />
                      </label>
                      <Input
                        placeholder={t('projectWizard.targetUrlPlaceholder')}
                        value={targetUrl}
                        onChange={(e) => setTargetUrl(e.target.value)}
                      />
                      <p className="text-xs text-surface-500 mt-1">
                        {t('projectWizard.targetUrlHint')}
                      </p>
                    </div>
                  </div>
                </MotionCard>

                {/* Repo Selection */}
                <MotionCard {...anim(1)}>
                  <h3 className="text-lg font-semibold text-surface-100 mb-1 flex items-center gap-2">
                    <Github className="w-5 h-5 text-brand-400" />
                    {t('projectWizard.selectRepo')}
                    <Tooltip text={t('projectWizard.selectRepoTip')} />
                  </h3>
                  <p className="text-sm text-surface-400 mb-4">
                    {t('projectWizard.selectRepoSimple')}
                  </p>

                  {/* Search */}
                  <div className="relative mb-3">
                    <Input
                      placeholder={t('projectWizard.searchRepos')}
                      value={repoSearch}
                      onChange={(e) => setRepoSearch(e.target.value)}
                      leftIcon={<Search className="w-4 h-4" />}
                    />
                  </div>

                  {/* Repo list */}
                  <div className="max-h-72 overflow-y-auto rounded-xl border border-surface-700 bg-surface-800/30 divide-y divide-surface-800/50">
                    {reposLoading ? (
                      <div className="p-6 text-sm text-surface-400 text-center flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('common.loading')}
                      </div>
                    ) : filteredRepos && filteredRepos.length > 0 ? (
                      filteredRepos.map((repo: any) => {
                        const fullName = repo.full_name || repo.fullName;
                        const isSelected = repoFullName === fullName;
                        return (
                          <button
                            key={repo.id || fullName}
                            type="button"
                            onClick={() => {
                              setRepoFullName(fullName);
                              setBranch(''); // Reset so auto-detect picks default
                            }}
                            className={`w-full text-left px-4 py-3 hover:bg-surface-700/50 transition-all flex items-center justify-between ${
                              isSelected ? 'bg-brand-500/10 border-l-2 border-l-brand-500' : ''
                            }`}
                          >
                            <div>
                              <span className="text-sm font-medium text-surface-200">
                                {fullName}
                              </span>
                              {(repo.language || repo.description) && (
                                <p className="text-xs text-surface-500 mt-0.5">
                                  {repo.language && (
                                    <span className="mr-2 text-surface-400">{repo.language}</span>
                                  )}
                                  {repo.description?.substring(0, 80)}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {repo.private && <Lock className="w-3.5 h-3.5 text-surface-500" />}
                              {isSelected && <CheckCircle2 className="w-5 h-5 text-brand-400" />}
                            </div>
                          </button>
                        );
                      })
                    ) : (
                      <div className="p-6 text-sm text-surface-500 text-center">
                        {t('projectWizard.noRepos')}
                      </div>
                    )}
                  </div>
                </MotionCard>

                {/* Branch selector — only if repo is selected */}
                {repoFullName && (
                  <MotionCard {...anim(2)}>
                    <h3 className="text-sm font-semibold text-surface-100 mb-2 flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-brand-400" />
                      {t('projectWizard.selectBranch')}
                      <Tooltip text={t('projectWizard.branchTip')} />
                    </h3>
                    {branchesLoading ? (
                      <div className="flex items-center gap-2 text-sm text-surface-400 py-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('projectWizard.loadingBranches')}
                      </div>
                    ) : (
                      <Select value={branch} onValueChange={setBranch}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('projectWizard.selectBranch')} />
                        </SelectTrigger>
                        <SelectContent>
                          {branches && branches.length > 0 ? (
                            branches.map((b: any) => (
                              <SelectItem key={b.name} value={b.name}>
                                {b.name}
                                {b.protected ? ' 🔒' : ''}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="__none" disabled>
                              {t('projectWizard.noBranches')}
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                    <p className="text-xs text-surface-500 mt-1.5 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      {t('projectWizard.branchHint')}
                    </p>
                  </MotionCard>
                )}

                {/* Info box: What the AI agent will do */}
                <MotionCard {...anim(3)} className="border-brand-500/10 bg-brand-500/[0.02]">
                  <div className="flex items-start gap-3">
                    <Brain className="w-5 h-5 text-brand-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-surface-200 mb-1">
                        {t('projectWizard.whatAgentDoes')}
                      </h4>
                      <ul className="text-xs text-surface-400 space-y-1 list-disc list-inside">
                        <li>{t('projectWizard.agentBullet1')}</li>
                        <li>{t('projectWizard.agentBullet2')}</li>
                        <li>{t('projectWizard.agentBullet3')}</li>
                        <li>{t('projectWizard.agentBullet4')}</li>
                        <li>{t('projectWizard.agentBullet5')}</li>
                      </ul>
                    </div>
                  </div>
                </MotionCard>

                {/* Launch button */}
                <div className="flex justify-end pt-2">
                  <Button
                    onClick={handleLaunch}
                    disabled={!canLaunch}
                    className="px-10 py-3 text-base"
                  >
                    <Sparkles className="w-5 h-5" />
                    {t('projectWizard.launchAgent')}
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </div>
              </>
            )}
          </motion.div>
        )}

        {/* ═══ STEP 2: AI Agent Working ══════════════════ */}
        {step === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="flex flex-col items-center justify-center py-16"
          >
            {analyzeAndCreate.isPending ? (
              <div className="text-center space-y-8 max-w-lg">
                {/* Animated brain */}
                <div className="relative w-28 h-28 mx-auto">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-brand-500/40 to-purple-500/40 animate-pulse" />
                  <div className="absolute inset-1 rounded-full bg-gradient-to-br from-brand-600/20 to-purple-600/20 animate-spin [animation-duration:8s]" />
                  <div className="absolute inset-3 rounded-full bg-surface-900 flex items-center justify-center">
                    <Brain className="w-10 h-10 text-brand-400 animate-pulse" />
                  </div>
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      className="absolute w-2.5 h-2.5 rounded-full bg-brand-400"
                      animate={{
                        x: [0, 40 * Math.cos((i * 2 * Math.PI) / 3), 0],
                        y: [0, 40 * Math.sin((i * 2 * Math.PI) / 3), 0],
                        opacity: [0.3, 1, 0.3],
                      }}
                      transition={{ duration: 3, repeat: Infinity, delay: i * 1 }}
                      style={{ top: '50%', left: '50%', marginTop: -5, marginLeft: -5 }}
                    />
                  ))}
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-surface-100 mb-2">
                    {t('projectWizard.agentTitle')}
                  </h3>
                  <p className="text-sm text-surface-400">
                    {t('projectWizard.agentSubtitle', { repo: repoFullName })}
                  </p>
                </div>

                {/* Phase progress */}
                <div className="space-y-3 text-left w-full">
                  {AI_PHASES.map((phase, i) => {
                    const Icon = phase.icon;
                    const isDone = i < aiPhase;
                    const isCurrent = i === aiPhase;
                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: i <= aiPhase ? 1 : 0.3, x: 0 }}
                        transition={{ delay: i * 0.15 }}
                        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all ${
                          isCurrent
                            ? 'bg-brand-500/10 border border-brand-500/20'
                            : isDone
                              ? 'bg-surface-800/30'
                              : ''
                        }`}
                      >
                        {isDone ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                        ) : isCurrent ? (
                          <Loader2 className="w-5 h-5 text-brand-400 animate-spin flex-shrink-0" />
                        ) : (
                          <Icon className="w-5 h-5 text-surface-600 flex-shrink-0" />
                        )}
                        <span
                          className={`text-sm ${
                            isCurrent
                              ? 'text-brand-300 font-medium'
                              : isDone
                                ? 'text-surface-300'
                                : 'text-surface-600'
                          }`}
                        >
                          {phase.label}
                        </span>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ) : analyzeAndCreate.isError ? (
              <div className="text-center space-y-6 max-w-md">
                <XCircle className="w-16 h-16 text-red-400 mx-auto" />
                <h3 className="text-xl font-semibold text-surface-100">
                  {t('projectWizard.analysisFailed')}
                </h3>
                <p className="text-sm text-surface-400">{analyzeAndCreate.error?.message}</p>
                <div className="flex gap-3 justify-center">
                  <Button variant="ghost" onClick={() => setStep(1)}>
                    <ArrowLeft className="w-4 h-4" />
                    {t('common.back')}
                  </Button>
                  <Button
                    onClick={() => {
                      setAiPhase(0);
                      analyzeAndCreate.mutate();
                    }}
                  >
                    <Sparkles className="w-4 h-4" />
                    {t('projectWizard.retry')}
                  </Button>
                </div>
              </div>
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
